import React, { FC } from "react";
import styled from "styled-components";
import { ThemeProps } from "../../styles/theme";
import { RouteComponentProps } from "@reach/router";
import { usePageTitle } from "../../hooks/usePageTitle";
import { CardMarkupViewer } from "../../markup/cardMarkupViewer/cardMarkupViewer";
import { CardMarkupViewerState } from "../../markup/cardMarkupViewer/cardMarkupViewer.interface";

const Root = styled.div<ThemeProps>`
    padding: 20px;
    color: ${(props: ThemeProps) => props.theme.primaryText};
    background-color: ${(props: ThemeProps) => props.theme.primaryBackground};
    max-width: 100%;
    overflow-x: hidden;
    
    @media (max-width: 768px) {
        padding: 10px;
    }
`;

const Title = styled.h1<ThemeProps>`
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid ${(props: ThemeProps) => props.theme.primaryBorder};
    padding-bottom: 0.5rem;
    color: ${(props: ThemeProps) => props.theme.primaryText};
    
    @media (max-width: 768px) {
        font-size: 2rem;
        margin-bottom: 1rem;
    }
    
    @media (max-width: 480px) {
        font-size: 1.75rem;
    }
`;

const Description = styled.p<ThemeProps>`
    font-size: 1.1rem;
    line-height: 1.5;
    margin-bottom: 2rem;
    color: ${(props: ThemeProps) => props.theme.primaryText};
    
    @media (max-width: 768px) {
        font-size: 1rem;
        margin-bottom: 1.5rem;
    }
`;

const SectionTitle = styled.h2<ThemeProps>`
    font-size: 2rem;
    margin-top: 2rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid ${(props: ThemeProps) => props.theme.primaryBorder};
    padding-bottom: 0.5rem;
    color: ${(props: ThemeProps) => props.theme.primaryText};
    
    @media (max-width: 768px) {
        font-size: 1.5rem;
        margin-top: 1.5rem;
    }
    
    @media (max-width: 480px) {
        font-size: 1.25rem;
    }
`;

const TagsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 15px;
    }
`;

const TagCard = styled.div<ThemeProps>`
    border: 1px solid ${(props: ThemeProps) => props.theme.primaryBorder};
    border-radius: 8px;
    padding: 15px;
    background-color: ${(props: ThemeProps) => props.theme.reviewCardBackground};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 100%;
    box-sizing: border-box;
    
    @media (max-width: 768px) {
        padding: 10px;
    }
`;

const TagName = styled.h3<ThemeProps>`
    font-size: 1.3rem;
    margin-bottom: 10px;
    color: ${(props: ThemeProps) => props.theme.primaryText};
    
    @media (max-width: 768px) {
        font-size: 1.2rem;
    }
`;

const TagDescription = styled.p<ThemeProps>`
    margin-bottom: 10px;
    line-height: 1.4;
    color: ${(props: ThemeProps) => props.theme.primaryText};
`;

const Example = styled.pre<ThemeProps>`
    background-color: ${(props: ThemeProps) => props.theme.secondaryBackground};
    color: ${(props: ThemeProps) => props.theme.primaryText};
    padding: 10px;
    border-radius: 4px;
    font-family: monospace;
    white-space: pre;
    overflow-x: auto;
    font-size: 14px;
    max-width: 100%;
    box-sizing: border-box;
    
    @media (max-width: 768px) {
        font-size: 12px;
        padding: 8px;
    }
`;

const ExampleTitle = styled.div<ThemeProps>`
    font-weight: bold;
    margin-bottom: 5px;
    color: ${(props: ThemeProps) => props.theme.primaryText};
`;

const PreviewContainer = styled.div<ThemeProps>`
    margin-top: 15px;
    border: 1px solid ${(props: ThemeProps) => props.theme.primaryBorder};
    border-radius: 4px;
    padding: 15px;
    background-color: ${(props: ThemeProps) => props.theme.primaryBackground};
    max-width: 100%;
    overflow-x: auto;
    box-sizing: border-box;
    
    @media (max-width: 768px) {
        padding: 8px;
    }
`;

const PreviewTitle = styled.div<ThemeProps>`
    font-weight: bold;
    margin-bottom: 10px;
    color: ${(props: ThemeProps) => props.theme.primaryText};
    
    @media (max-width: 768px) {
        margin-bottom: 5px;
    }
`;

const TableOfContents = styled.div<ThemeProps>`
    margin: 20px 0;
    padding: 15px;
    border: 1px solid ${(props: ThemeProps) => props.theme.primaryBorder};
    border-radius: 8px;
    background-color: ${(props: ThemeProps) => props.theme.secondaryBackground};
    
    @media (max-width: 768px) {
        padding: 10px;
    }
`;

const TocTitle = styled.div<ThemeProps>`
    font-weight: bold;
    font-size: 1.1rem;
    margin-bottom: 10px;
    color: ${(props: ThemeProps) => props.theme.primaryText};
`;

const TocList = styled.ul<ThemeProps>`
    list-style-type: none;
    padding-left: 0;
    margin: 0;
`;

const TocSection = styled.li<ThemeProps>`
    margin-bottom: 10px;
    font-weight: bold;
`;

const TocItem = styled.li<ThemeProps>`
    margin: 5px 0 5px 15px;
    
    @media (max-width: 768px) {
        margin-left: 10px;
    }
`;

const TocLink = styled.a<ThemeProps>`
    color: ${(props: ThemeProps) => props.theme.accentColor};
    text-decoration: none;
    
    &:hover {
        text-decoration: underline;
    }
`;

const MarkupReference: FC<RouteComponentProps> = () => {
    usePageTitle('Markup Reference · Memkeep');
    // Примеры разметки
    const answerExample = `What is the capital of France?

{answer}
Paris`;

    const hintExample = `What is the capital of France?

{hint}
It starts with 'P' and ends with 's'.

{answer}
Paris`;

    const footerExample = `What is the capital of France?

{answer}
Paris

{footer}
From Geography 101 course`;

    const examplesExample = `What is the Greek word "αποζητώ προσοχή"?

{answer}
жажду внимания

{examples}
О σκύλος αποζητά την προσοχή σου όταν είσαι σπίτι. (Собака жаждет твоего внимания, когда ты дома.)`;

    const referenceExample = `What is the Greek definite article?

{answer}
ο / η / το

{reference}
Articles
ο | masculine
η | feminine
το | neuter
===
Case endings
-ος | nominative
-ου | genitive`;

    const subcardsExample = `Conditionals type 1, 2 and 3

{answer}
First, Second and Third Conditional

{subcards}
@First Conditional
If it ___ tomorrow, I ___ at home.
---
If it **rains** tomorrow, I **will stay** at home.
===
@Second Conditional
If I ___ more time, I ___ more.
---
If I **had** more time, I **would travel** more.`;

    const tagsExample = `What is the capital of France?

{answer}
Paris

{tags}
geography, europe, capitals`;

    const imageExample = `What flag is this?

{image-123}

{answer}
The flag of France`;

    const codeExample = `How do you print "Hello World" in Python?

{answer}

{code}
print("Hello World")
{code}`;

    const iconExample = `What does this icon mean?

{icon-warning}

{answer}
It's a warning symbol`;

    return (
        <Root>
            <Title>Markup Reference</Title>
            <Description>
                This guide explains all the markup tags available for formatting cards in MemKeep. 
                Use these tags to structure your cards with questions, hints, answers, and formatting.
            </Description>

            <TableOfContents>
                <TocTitle>Table of Contents</TocTitle>
                <TocList>
                    <TocSection>
                        <TocLink href="#card-structure">Card Structure</TocLink>
                        <TocList>
                            <TocItem><TocLink href="#answer-tag">{'{answer}'}</TocLink></TocItem>
                            <TocItem><TocLink href="#hint-tag">{'{hint}'}</TocLink></TocItem>
                            <TocItem><TocLink href="#footer-tag">{'{footer}'}</TocLink></TocItem>
                            <TocItem><TocLink href="#tags-tag">{'{tags}'}</TocLink></TocItem>
                            <TocItem><TocLink href="#examples-tag">{'{examples}'}</TocLink></TocItem>
                            <TocItem><TocLink href="#reference-tag">{'{reference}'}</TocLink></TocItem>
                            <TocItem><TocLink href="#subcards-tag">{'{subcards}'}</TocLink></TocItem>
                        </TocList>
                    </TocSection>
                    <TocSection>
                        <TocLink href="#formatting">Formatting</TocLink>
                        <TocList>
                            <TocItem><TocLink href="#image-tag">{'{image-<id>}'}</TocLink></TocItem>
                            <TocItem><TocLink href="#code-tag">{'{code}'}</TocLink></TocItem>
                            <TocItem><TocLink href="#icon-tag">{'{icon-<name>}'}</TocLink></TocItem>
                        </TocList>
                    </TocSection>
                </TocList>
            </TableOfContents>

            <SectionTitle id="card-structure">Card Structure</SectionTitle>
            <TagsContainer>
                <TagCard id="answer-tag">
                    <TagName>{'{answer}'}</TagName>
                    <TagDescription>Marks the beginning of the answer section.</TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{answerExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer 
                                markup={answerExample} 
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>

                <TagCard id="hint-tag">
                    <TagName>{'{hint}'}</TagName>
                    <TagDescription>Marks the beginning of the hint section (optional).</TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{hintExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer 
                                markup={hintExample} 
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>

                <TagCard id="footer-tag">
                    <TagName>{'{footer}'}</TagName>
                    <TagDescription>Marks the beginning of the footer section (optional).</TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{footerExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer 
                                markup={footerExample} 
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>

                <TagCard id="tags-tag">
                    <TagName>{'{tags}'}</TagName>
                    <TagDescription>Marks the beginning of the tags section (optional).</TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{tagsExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer 
                                markup={tagsExample} 
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>

                <TagCard id="examples-tag">
                    <TagName>{'{examples}'}</TagName>
                    <TagDescription>Marks the beginning of the examples section (optional).</TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{examplesExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer
                                markup={examplesExample}
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>

                <TagCard id="reference-tag">
                    <TagName>{'{reference}'}</TagName>
                    <TagDescription>
                        Optional reference tables shown only after the answer is revealed.
                        Each section starts with a title line; following lines are two-column
                        rows written as <code>left | right</code>. Sections are separated by <code>===</code>.
                    </TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{referenceExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer
                                markup={referenceExample}
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>

                <TagCard id="subcards-tag">
                    <TagName>{'{subcards}'}</TagName>
                    <TagDescription>
                        Optional mini flashcards reviewed one at a time. Subcards are
                        separated by <code>===</code>; within a subcard the question and
                        answer are separated by <code>---</code>. Start a subcard with an
                        optional <code>@Group name</code> line to assign it to a group:
                        during review subcards are shown round-robin across groups (so you
                        never get two in a row from the same group while another group has
                        unseen cards), and the review bar shows how many groups you have
                        covered so far.
                    </TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{subcardsExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer
                                markup={subcardsExample}
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>
            </TagsContainer>

            <SectionTitle id="formatting">Formatting</SectionTitle>
            <TagsContainer>
                <TagCard id="image-tag">
                    <TagName>{'{image-<id>}'}</TagName>
                    <TagDescription>Used for including images with a specific ID.</TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{imageExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer 
                                markup={imageExample} 
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>

                <TagCard id="code-tag">
                    <TagName>{'{code}'}</TagName>
                    <TagDescription>Used for formatting code snippets.</TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{codeExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer 
                                markup={codeExample} 
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>

                <TagCard id="icon-tag">
                    <TagName>{'{icon-<name>}'}</TagName>
                    <TagDescription>Used for adding icons.</TagDescription>
                    <ExampleTitle>Example:</ExampleTitle>
                    <Example>{iconExample}</Example>
                    <PreviewContainer>
                        <PreviewTitle>Preview:</PreviewTitle>
                        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <CardMarkupViewer 
                                markup={iconExample} 
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                            />
                        </div>
                    </PreviewContainer>
                </TagCard>
            </TagsContainer>
        </Root>
    );
}

export default MarkupReference;
